import type {
  ProcessDataStatusProfile,
  ProcessDataStatusProfileId,
  ProcessDataStatusRouteDef,
} from './process-data-status.types.js';

/** Tier 4 자연바다식품 — 메뉴 path 는 공통, processTypeCode·화면 메타만 JYBD 시드에 맞춤. */
export const JAYEON_BADA_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  washing: {
    processTypeCode: 'WASH',
    screen: {
      title: '세척공정현황',
      description: '세척기 3기 세척수 온도(CCP)·세척시간·세척횟수 PLC 자동수집',
      primaryChartTitle: '세척수 온도 추이 (설비별)',
      primaryParamKey: 'water_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '세척시간·횟수',
      secondaryParamKey: 'wash_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'wash',
      gridParamLabels: {
        water_temp_c: '세척수 온도',
        wash_time_min: '세척시간',
        wash_count: '세척횟수',
      },
    },
  },
  'broth-heating': {
    processTypeCode: 'BROTH_HEATING',
    screen: {
      title: '육수가열공정현황',
      description: '기존·신규 가열탱크 핵심온도(CCP) 및 가열시간 모니터링',
      primaryChartTitle: '가열온도 추이 (탱크별)',
      primaryParamKey: 'core_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '가열시간',
      secondaryParamKey: 'heat_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        core_temp_c: '가열온도',
        heat_time_min: '가열시간',
      },
    },
  },
  'metal-detection': {
    processTypeCode: 'METAL_DETECT',
    screen: {
      title: '금속검출공정현황',
      description: '금속검출기 PASS/FAIL 이력·가동상태·가동시간',
      primaryChartTitle: '검출 결과 (PASS/FAIL)',
      primaryParamKey: 'metal_passed',
      primaryChartKind: 'pass-fail-bar',
      secondaryChartTitle: '설비 가동시간',
      secondaryParamKey: 'run_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'metal',
      gridParamLabels: {
        metal_passed: '검출결과',
        inspect_history: '검사이력',
        run_time_min: '가동시간',
        run_status: '가동상태',
      },
    },
  },
  'inner-pack': {
    processTypeCode: 'INNER_PACKING',
    screen: {
      title: '내포장공정현황',
      description: '자동 포장설비 포장량·가동시간·가동상태',
      primaryChartTitle: '포장량 누적',
      primaryParamKey: 'pack_count',
      primaryChartKind: 'single-line',
      primaryChartUnit: 'Box',
      secondaryChartTitle: '가동상태 분포',
      secondaryParamKey: 'run_status',
      secondaryChartKind: 'status-bar',
      kpiKind: 'pack',
      gridParamLabels: {
        pack_count: '포장량',
        run_time_min: '가동시간',
        run_status: '가동상태',
      },
    },
  },
};

/** Tier 3 EGM 김치공장 — 동일 메뉴 슬롯, processTypeCode·파라미터는 kimchi-demo 시드. */
export const KIMCHI_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  washing: {
    processTypeCode: 'K_WASH',
    screen: {
      title: '세척공정현황',
      description: '배추 버블세척기 세척수 온도(CCP)·유량·수압 PLC 자동수집',
      primaryChartTitle: '세척수 온도 추이',
      primaryParamKey: 'wash_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '세척수 유량',
      secondaryParamKey: 'wash_flow_lpm',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'L/min',
      kpiKind: 'wash',
      gridParamLabels: {
        wash_temp_c: '세척수 온도',
        wash_flow_lpm: '세척수 유량',
        wash_pressure_bar: '수압',
        run_status: '가동상태',
      },
    },
  },
  'broth-heating': {
    processTypeCode: 'K_BRINE',
    screen: {
      title: '절임공정현황',
      description: '절임 사일로 온도·염도(CCP)·액위 모니터링',
      primaryChartTitle: '절임온도 추이',
      primaryParamKey: 'brine_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '염도',
      secondaryParamKey: 'salinity_pct',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '%',
      kpiKind: 'brine',
      gridParamLabels: {
        brine_temp_c: '절임온도',
        salinity_pct: '염도',
        brine_level_pct: '액위',
        cover_status: '덮개상태',
      },
    },
  },
  'metal-detection': {
    processTypeCode: 'K_METAL',
    screen: {
      title: '금속검출공정현황',
      description: '금속검출기 PASS/FAIL·검출횟수·감도',
      primaryChartTitle: '검출 결과 (PASS/FAIL)',
      primaryParamKey: 'metal_passed',
      primaryChartKind: 'pass-fail-bar',
      secondaryChartTitle: '검출횟수',
      secondaryParamKey: 'detect_count',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'EA',
      kpiKind: 'metal',
      gridParamLabels: {
        metal_passed: '통과상태',
        detect_count: '검출횟수',
        sensitivity_level: '감도레벨',
      },
    },
  },
  'inner-pack': {
    processTypeCode: 'K_PACK',
    screen: {
      title: '포장공정현황',
      description: '중량·진공 포장설비 중량(CCP)·포장수량·가동상태',
      primaryChartTitle: '포장수량 추이 (설비별)',
      primaryParamKey: 'pack_count',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: 'EA',
      secondaryChartTitle: '포장중량',
      secondaryParamKey: 'pack_weight_g',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'g',
      kpiKind: 'pack',
      gridParamLabels: {
        pack_weight_g: '포장중량',
        pack_count: '포장수량',
        reject_count: '중량불량',
        run_status: '가동상태',
      },
    },
  },
};

export const PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'washing',
    name: 'mes.data-status.washing',
    menuCode: 'mes.data-status.washing',
    routeKey: 'washing',
  },
  {
    path: 'broth-heating',
    name: 'mes.data-status.broth-heating',
    menuCode: 'mes.data-status.broth-heating',
    routeKey: 'broth-heating',
  },
  {
    path: 'metal-detection',
    name: 'mes.data-status.metal-detection',
    menuCode: 'mes.data-status.metal-detection',
    routeKey: 'metal-detection',
  },
  {
    path: 'inner-pack',
    name: 'mes.data-status.inner-pack',
    menuCode: 'mes.data-status.inner-pack',
    routeKey: 'inner-pack',
  },
];

const PROFILE_MAP: Record<ProcessDataStatusProfileId, ProcessDataStatusProfile> = {
  'jayeon-bada': JAYEON_BADA_PROCESS_DATA_STATUS_PROFILE,
  kimchi: KIMCHI_PROCESS_DATA_STATUS_PROFILE,
};

export function resolveProcessDataStatusProfile(
  profileId: ProcessDataStatusProfileId,
): ProcessDataStatusProfile {
  return PROFILE_MAP[profileId];
}
